import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@im/react-localization';
import {
  SearchBar,
  Props as SearchBarProps,
} from 'src/components/AutoSearch/AutoSearch';
import {
  Product,
  ProductByGlobalPlanId,
  ServicePlan,
  useProductListByGlobalPlanId,
  useProductsQueryParams,
  useSearchByServicePlan,
} from 'src/views/productGroups/hooks/useProducts';
import upperFirst from 'lodash/upperFirst';

export interface ProductSearchBarProps {
  onSelected: (productList: ProductByGlobalPlanId[]) => void;
}

const ProductSearchBar: FC<ProductSearchBarProps> = ({ onSelected }) => {
  const translate = useTranslate();

  const [servicePlan, setServicePlan] = useState<ServicePlan | undefined>(
    undefined
  );
  const mutation = useProductListByGlobalPlanId();

  useEffect(() => {
    if (servicePlan) {
      mutation
        .mutateAsync({
          globalPlanId: servicePlan.global_plan_id,
          page: 1,
          pageSize: 100,
        })
        .then((res) => onSelected(res.data))
        .catch(() => onSelected([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicePlan]);
  const options = useMemo(
    () =>
      ['name', 'global_plan_id'].map((value) => ({
        label: translate(`ProductGroups.${upperFirst(value)}`),
        value,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate, translate('ProductGroups.Name')]
  );

  const { filter, searchQuery, setFilter } = useProductsQueryParams({
    pageSize: 200,
  });
  const { data, isLoading } = useSearchByServicePlan(searchQuery);

  const searchBarProps: SearchBarProps = {
    autoCompleteProps: {
      getOptionLabel: (v: any) =>
        typeof v === 'string' ? v : `${v.name} - ${v.global_plan_id}`,
      loading: isLoading,
      loadingText: translate('Common.Loading'),
      onChange: (_: any, servicePlan: ServicePlan, reason: string) => {
        if (reason === 'selectOption') {
          setServicePlan(servicePlan);
        }
      },
      options: data?.data ?? [],
    },
    dropdownOptions: options,
    onDropdownOptionChange: (field) =>
      setFilter({
        field: field?.value as unknown as keyof Product,
        value: '',
      }),
    onSearch: ({ dropdownTerm: field, inputValue: value }) =>
      setFilter({ field: field as unknown as keyof Product, value }),
    value: filter?.value,
  };

  return <SearchBar {...searchBarProps} />;
};

export default ProductSearchBar;
